import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import Headline from 'uiKit/texts/Headline'
import Search from 'uiKit/Search/Search'
import Table from 'uiKit/table/Table'
import TableRow from 'uiKit/table/TableRow'
import TableCell from 'uiKit/table/TableCell'
import Pagination from 'uiKit/table/Pagination'
import DateControl from 'uiKit/DateControlNew'
import BreadCrumbs from 'uiKit/BreadCrumbs/BreadCrumbs'
import { sortingIcon } from 'uiKit/icons/Icons'
import LoaderScreen from 'uiKit/loaders/loaderScreen'
import LoaderOverlay from 'uiKit/loaders/loaderOverlay'
import NoChartData from 'uiKit/NoChartData'
import * as S from '../../Dashboard.style'
import {
  Container as TotalUsersContainer,
  FiltersRow,
  PaginationSpacer,
} from '../TotalUsers/TotalUsers.style'
import { loadTopQueriedTopics, exportTopQueriedTopicsCsv } from '../../api/dashboard'
import { DateControlContext } from 'contexts/DateControlContext'
import { getDashboardDateApiParams } from '../../helpers/dashboardDateParams'
import { useScrollToTop } from 'hooks/useScrollToTop'
import { canViewCarecoDashboard } from '../../../../security'
import { isQcsBotPayload } from '../../../../constants/qcs'

type Row = { id: number; name: string; people: number; count: number }

type Props = RouteComponentProps & { activeBot?: any, adminUser?: any }

export const TotalQueriedTopics: React.FC<Props> = ({ history, match, activeBot, adminUser }) => {
  useScrollToTop()

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [page, setPage] = useState(0)
  const [rows, setRows] = useState<Row[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [sortBy, setSortBy] = useState<'people' | 'query'>('query')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const {
    dateRange: { startDate, endDate },
    frequency,
  } = useContext(DateControlContext) as any
  const prevDatesRef = useRef({ start: startDate?.valueOf?.(), end: endDate?.valueOf?.() })
  const lastFetchKeyRef = useRef<string | null>(null)
  const { botId: routeBotId } = (match as any)?.params || {}
  const botId = routeBotId || activeBot?.id
  const isCarecoAdmin = useMemo(
    () => canViewCarecoDashboard(botId, adminUser?.botAccesses),
    [botId, adminUser],
  )
  const hideFrequency = isQcsBotPayload(activeBot)

  const buildRequestParams = (pageOverride = page) => {
    const params: any = {
      page: pageOverride + 1,
      size: 10,
      ...getDashboardDateApiParams(frequency, startDate, endDate, isCarecoAdmin),
      sortBy1: sortBy,
      sortDir1: sortDir,
    }
    if (debouncedQuery) params.search = debouncedQuery
    return params
  }

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(query)
      setPage(0)
    }, 500)
    return () => clearTimeout(t)
  }, [query])

  useEffect(() => {
    if (!botId) return

    const start = startDate?.valueOf?.()
    const end = endDate?.valueOf?.()
    const datesChanged = prevDatesRef.current.start !== start || prevDatesRef.current.end !== end
    const effectivePage = datesChanged ? 0 : page

    if (datesChanged) {
      prevDatesRef.current = { start, end }
      if (page !== 0) {
        setPage(0)
      }
    }

    const fetchKey = [botId, effectivePage, frequency, start, end, debouncedQuery, sortBy, sortDir].join('|')
    if (lastFetchKeyRef.current === fetchKey) return
    lastFetchKeyRef.current = fetchKey

    setLoading(true)

    loadTopQueriedTopics(botId, buildRequestParams(effectivePage))
      .then((res: any) => {
        if (res && Array.isArray(res.content)) {
          const mapped: Row[] = res.content.map((it: any, idx: number) => ({
            id: idx,
            name: it.topic,
            people: Number(it.peopleCount) || 0,
            count: Number(it.queryCount) || 0,
          }))
          setRows(mapped)
          setTotalCount(Number(res.totalElements) || 0)
        } else {
          setRows([])
          setTotalCount(0)
        }
      })
      .catch(() => {
        setRows([])
        setLoading(false)
        setTotalCount(0)
      })
      .finally(() => setLoading(false))
  }, [botId, page, startDate, endDate, frequency, debouncedQuery, sortBy, sortDir])

  useEffect(() => {
    setPage(0)
    lastFetchKeyRef.current = null
  }, [botId])

  const handleSortPeople = () => {
    if (sortBy === 'people') {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy('people')
      setSortDir('desc')
    }
    setPage(0)
  }

  const handleSortQuery = () => {
    if (sortBy === 'query') {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy('query')
      setSortDir('desc')
    }
    setPage(0)
  }

  const handleDashboardClick = () => {
    const currentUrl = (match as any)?.url || ''
    const dashboardBase = currentUrl.replace(/\/top-queried-topics$/, '') || '/dashboard'
    history.push(dashboardBase)
  }

  const handleExport = async () => {
    if (!botId) return
    try {
      setIsExporting(true)
      const params: any = {
        ...getDashboardDateApiParams(frequency, startDate, endDate, isCarecoAdmin),
        sortBy1: sortBy,
        sortDir1: sortDir,
      }
      if (debouncedQuery) params.search = debouncedQuery

      const blob = await exportTopQueriedTopicsCsv(botId, params)
      if (!blob) return
      const url = window.URL.createObjectURL(blob as any)
      const a = document.createElement('a')
      a.href = url
      a.download = 'top-queried-topics.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } finally {
      setIsExporting(false)
    }
  }

  const columnTitles = useMemo(
    () => [
      { title: 'Query name', maxWidth: 360 },
      {
        component: (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
            onClick={handleSortPeople}
          >
            <span>Number of people queried</span>
            {sortingIcon()}
          </div>
        ),
        maxWidth: 240,
      },
      {
        component: (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
            onClick={handleSortQuery}
          >
            <span>Query count</span>
            {sortingIcon()}
          </div>
        ),
        maxWidth: 180,
      },
    ],
    [sortBy, sortDir],
  )

  const searchInputStyle = {
    borderRadius: 8,
    width: '327px',
    height: '45px',
    padding: '14px 44px 19px 24px',
    minWidth: 240,
    flex: '0 1 327px',
    border: '1px solid rgba(226, 232, 234, 1)',
  }

  const renderBody = () => {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <LoaderScreen size={{ circleSize: 60, border: 8 }} />
        </div>
      )
    }

    if (rows.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0' }}>
          <NoChartData text={'No topics found'} isUserData={true} />
        </div>
      )
    }
    const formatTopic = (t: string) => {
      const cleaned = (t || '').replaceAll('_', ' ').toLowerCase()
      return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : ''
    }
    return (
      <>
        <Table titles={columnTitles}>
          {rows.map((row, idx) => (
            <TableRow key={row.id} style={{ background: idx % 2 ? 'var(--color-table-row-primary)' : undefined }}>
              <TableCell style={{ maxWidth: columnTitles[0].maxWidth }}>{formatTopic(row.name)}</TableCell>
              <TableCell style={{ maxWidth: columnTitles[1].maxWidth }}>{row.people}</TableCell>
              <TableCell style={{ maxWidth: columnTitles[2].maxWidth }}>{row.count}</TableCell>
            </TableRow>
          ))}
        </Table>

        <PaginationSpacer>
          <Pagination page={page} rowsPerPage={10} count={totalCount} onChangePage={(_, next) => setPage(next)} />
        </PaginationSpacer>
      </>
    )
  }

  return (
    <TotalUsersContainer>
      {isExporting && <LoaderOverlay />}
      <PaginationSpacer mt={30}>
        <BreadCrumbs items={[{ label: 'Dashboard', onClick: handleDashboardClick }, { label: 'Top queried topics' }]} />
      </PaginationSpacer>
      <S.HeadlineWrap>
        <Headline title={`Top queried topics (${totalCount})`} />
        <DateControl
          isCarecoAdmin={isCarecoAdmin}
          handleExport={isCarecoAdmin ? handleExport : undefined}
          hideFrequency={hideFrequency}
        />
      </S.HeadlineWrap>
      <FiltersRow>
        <Search
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search by name"
          inputStyle={searchInputStyle}
        />
      </FiltersRow>

      {renderBody()}
    </TotalUsersContainer>
  )
}

const mapStateToProps = (state: any) => ({
  activeBot: state.activeBot,
  adminUser: state.adminUser,
})

export default withRouter(connect(mapStateToProps)(TotalQueriedTopics))
